import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Web3Singleton from 'web3Singleton';

import { IState } from 'models/reducers/i-state.interface';
import { IWalletCurrency } from 'models/wallet-currency-response.interface';

import { ERROR_MESSAGES } from 'constants/error-messages.constant';
import { NETWORK_SETTINGS } from 'constants/network.constant';
import { getActiveNetworkOverride } from 'constants/runtime-network';

import { addErrorMessageAction } from 'actions/global-actions';

import { StakingContractType } from 'enums/staking-contract-type.enum';

import { isDevImpersonationActive } from 'utils/dev-impersonation.utils';
import { getStakingContractAddressByType } from 'utils/get-staking-contract-address-by-type.utils';
import { parseToDecimal } from 'utils/parser.utils';

import { hasStuckVipDeposits } from '../overrides/stuck-vip-deposits';
import {
  DepositActionParams,
  StakeParams,
  TransactionState,
  TransactionStatus,
} from '../types';

const initialTransactionState: TransactionState = {
  status: TransactionStatus.IDLE,
  hash: null,
  error: null,
};

/**
 * Hook for executing staking actions (stake, claim, withdraw).
 * Handles transaction lifecycle and error states.
 *
 * @param onSuccess - Callback to execute after successful transaction (e.g., refetch data)
 */
export const useStakingActions = (
  onSuccess: () => void
) => {
  const { t } = useTranslation('notifications');
  const dispatch = useDispatch();

  const walletAddress = useSelector((state: IState) => state.wallet.address);
  const lgoWeb3 = Web3Singleton.getInstance();

  const [transaction, setTransaction] = useState<TransactionState>(initialTransactionState);

  const clearTransaction = useCallback(() => {
    setTransaction(initialTransactionState);
  }, []);

  const setTransactionPending = useCallback(() => {
    setTransaction({
      status: TransactionStatus.PENDING,
      hash: null,
      error: null,
    });
  }, []);

  const handleTransactionHash = useCallback((hash: string) => {
    setTransaction({
      status: TransactionStatus.CONFIRMING,
      hash,
      error: null,
    });
  }, []);

  const handleTransactionSuccess = useCallback(() => {
    setTransaction((prev) => ({
      ...prev,
      status: TransactionStatus.SUCCESS,
    }));
    onSuccess();
  }, [onSuccess]);

  const handleTransactionError = useCallback(
    (error: unknown) => {
      const errorCode = (error as { code?: number })?.code;
      const errorMessage =
        errorCode === 4001
          ? t(ERROR_MESSAGES.purchaseCanceled)
          : t(ERROR_MESSAGES.default);

      setTransaction({
        status: TransactionStatus.ERROR,
        hash: null,
        error: errorMessage,
      });

      dispatch(addErrorMessageAction(errorMessage));
    },
    [dispatch, t]
  );

  /**
   * Checks and grants ERC20 permission if needed
   */
  const ensurePermission = useCallback(
    async (
      contractAddress: string,
      currency: IWalletCurrency,
      amount: string
    ): Promise<boolean> => {
      const amountInDecimals = parseToDecimal(amount, currency.decimal);

      try {
        setTransaction({
          status: TransactionStatus.APPROVING,
          hash: null,
          error: null,
        });

        const hasPermission = await lgoWeb3.checkPermissionToCurrency(
          contractAddress,
          walletAddress,
          currency as Required<IWalletCurrency>,
          +amountInDecimals
        );

        if (!hasPermission) {
          await lgoWeb3.grantCurrencyPermissionServiceWithAmount(
            contractAddress,
            currency as Required<IWalletCurrency>,
            walletAddress,
            amountInDecimals
          );
        }

        return true;
      } catch (error) {
        console.error('Permission grant failed:', error);
        const errorCode = (error as { code?: number })?.code;

        if (errorCode === 4001) {
          dispatch(addErrorMessageAction(t(ERROR_MESSAGES.permissionsRejected)));
        } else {
          dispatch(addErrorMessageAction(t(ERROR_MESSAGES.default)));
        }

        setTransaction({
          status: TransactionStatus.ERROR,
          hash: null,
          error: t(ERROR_MESSAGES.permissionsRejected),
        });

        return false;
      }
    },
    [lgoWeb3, walletAddress, dispatch, t]
  );

  /**
   * Stakes tokens in periodical staking
   */
  const stake = useCallback(
    async (params: StakeParams, currency: IWalletCurrency): Promise<boolean> => {
      if (isDevImpersonationActive()) {
        console.warn('[dev] Write blocked while impersonating');
        return false;
      }
      if (!walletAddress || !currency) {
        dispatch(addErrorMessageAction(t(ERROR_MESSAGES.default)));
        return false;
      }

      const contractAddress = NETWORK_SETTINGS.PERIODICAL_STAKE.ADDRESS;

      // Check and grant permission
      const hasPermission = await ensurePermission(
        contractAddress,
        currency,
        params.amount
      );

      if (!hasPermission) {
        return false;
      }

      // Execute stake
      setTransactionPending();

      const amountInDecimals = parseToDecimal(params.amount, currency.decimal);

      const callback = {
        onTransactionHash: handleTransactionHash,
        onConfirmation: handleTransactionSuccess,
      };

      try {
        await lgoWeb3.periodicalSafeStake(
          walletAddress,
          params.phaseIndex.toString(),
          params.periodDays,
          amountInDecimals,
          params.expectedApy,
          callback
        );
        return true;
      } catch (error) {
        console.error('Stake failed:', error);
        handleTransactionError(error);
        return false;
      }
    },
    [
      walletAddress,
      lgoWeb3,
      ensurePermission,
      setTransactionPending,
      handleTransactionHash,
      handleTransactionSuccess,
      handleTransactionError,
      dispatch,
      t,
    ]
  );

  /**
   * Stakes tokens in VIP staking (v0.3.1 contract)
   */
  const stakeVip = useCallback(
    async (params: StakeParams, currency: IWalletCurrency): Promise<boolean> => {
      if (isDevImpersonationActive()) {
        console.warn('[dev] Write blocked while impersonating');
        return false;
      }
      if (!walletAddress || !currency) {
        dispatch(addErrorMessageAction(t(ERROR_MESSAGES.default)));
        return false;
      }

      const contractAddress = NETWORK_SETTINGS.VIP_STAKE.ADDRESS;

      // Check and grant permission
      const hasPermission = await ensurePermission(
        contractAddress,
        currency,
        params.amount
      );

      if (!hasPermission) {
        return false;
      }

      // Execute stake
      setTransactionPending();

      const amountInDecimals = parseToDecimal(params.amount, currency.decimal);

      const callback = {
        onTransactionHash: handleTransactionHash,
        onConfirmation: handleTransactionSuccess,
      };

      try {
        await lgoWeb3.vipSafeStake(
          walletAddress,
          params.phaseIndex.toString(),
          params.periodDays,
          amountInDecimals,
          params.expectedApy,
          callback
        );
        return true;
      } catch (error) {
        console.error('VIP Stake failed:', error);
        handleTransactionError(error);
        return false;
      }
    },
    [
      walletAddress,
      lgoWeb3,
      ensurePermission,
      setTransactionPending,
      handleTransactionHash,
      handleTransactionSuccess,
      handleTransactionError,
      dispatch,
      t,
    ]
  );

  /**
   * Claims a single deposit
   */
  const claim = useCallback(
    async (params: DepositActionParams): Promise<boolean> => {
      if (isDevImpersonationActive()) {
        console.warn('[dev] Write blocked while impersonating');
        return false;
      }
      if (!walletAddress) {
        dispatch(addErrorMessageAction(t(ERROR_MESSAGES.default)));
        return false;
      }

      setTransactionPending();

      const callback = {
        onTransactionHash: handleTransactionHash,
        onConfirmation: handleTransactionSuccess,
      };

      try {
        if (params.isWhitelisted) {
          await lgoWeb3.whitelistedClaimInterest(
            walletAddress,
            params.depositId,
            callback
          );
        } else if (params.isVip) {
          await lgoWeb3.vipStakingClaim(
            walletAddress,
            params.depositId,
            callback
          );
        } else {
          await lgoWeb3.periodicalStakingClaim(
            walletAddress,
            params.depositId,
            callback
          );
        }
        return true;
      } catch (error) {
        console.error('Claim failed:', error);
        handleTransactionError(error);
        return false;
      }
    },
    [
      walletAddress,
      lgoWeb3,
      setTransactionPending,
      handleTransactionHash,
      handleTransactionSuccess,
      handleTransactionError,
      dispatch,
      t,
    ]
  );

  /**
   * Claims all available deposits
   * Handles periodical, whitelisted, and VIP deposits sequentially
   */
  const claimAll = useCallback(
    async (periodicalCount: number, whitelistedCount: number, vipCount: number): Promise<boolean> => {
      if (isDevImpersonationActive()) {
        console.warn('[dev] Write blocked while impersonating');
        return false;
      }
      if (!walletAddress) {
        dispatch(addErrorMessageAction(t(ERROR_MESSAGES.default)));
        return false;
      }

      // Defensive guard: the UI hides the Claim All button for stuck wallets,
      // but if any code path reaches this action vipStakingClaimAll would
      // revert on-chain. Mirror the UI predicate here so the action no-ops
      // instead of submitting a doomed transaction.
      const activeNetworkId =
        getActiveNetworkOverride()?.networkId ?? process.env.REACT_APP_NETWORK_ID ?? null;
      const activeVipContractAddress =
        getStakingContractAddressByType(StakingContractType.VIP) ?? null;
      if (hasStuckVipDeposits(walletAddress, activeNetworkId, activeVipContractAddress)) {
        console.warn('[staking] claimAll suppressed: wallet has stuck VIP deposits', {
          walletAddress,
        });
        return false;
      }

      const hasPeriodical = periodicalCount > 0;
      const hasWhitelisted = whitelistedCount > 0;
      const hasVip = vipCount > 0;

      if (!hasPeriodical && !hasWhitelisted && !hasVip) {
        return false;
      }

      setTransactionPending();

      const intermediateCallback = {
        onTransactionHash: handleTransactionHash,
        onConfirmation: () => {},
      };

      const finalCallback = {
        onTransactionHash: handleTransactionHash,
        onConfirmation: handleTransactionSuccess,
      };

      try {
        // Execute claim operations sequentially
        // Periodical first
        if (hasPeriodical) {
          const isPeriodicalLast = !hasWhitelisted && !hasVip;
          await lgoWeb3.periodicalStakingClaimAll(
            walletAddress,
            isPeriodicalLast ? finalCallback : intermediateCallback
          );
        }

        // Whitelisted second
        if (hasWhitelisted) {
          const isWhitelistedLast = !hasVip;
          await lgoWeb3.whitelistedClaimAllInterest(
            walletAddress,
            isWhitelistedLast ? finalCallback : intermediateCallback
          );
        }

        // VIP last
        if (hasVip) {
          await lgoWeb3.vipStakingClaimAll(walletAddress, finalCallback);
        }

        return true;
      } catch (error) {
        console.error('Claim all failed:', error);
        handleTransactionError(error);
        return false;
      }
    },
    [
      walletAddress,
      lgoWeb3,
      setTransactionPending,
      handleTransactionHash,
      handleTransactionSuccess,
      handleTransactionError,
      dispatch,
      t,
    ]
  );

  /**
   * Withdraws a deposit
   * Note: Early withdrawal may result in loss of rewards
   */
  const withdraw = useCallback(
    async (params: DepositActionParams): Promise<boolean> => {
      if (isDevImpersonationActive()) {
        console.warn('[dev] Write blocked while impersonating');
        return false;
      }
      if (!walletAddress) {
        dispatch(addErrorMessageAction(t(ERROR_MESSAGES.default)));
        return false;
      }

      setTransactionPending();

      const callback = {
        onTransactionHash: handleTransactionHash,
        onConfirmation: handleTransactionSuccess,
      };

      try {
        if (params.isWhitelisted) {
          await lgoWeb3.whitelistedWithdrawDeposit(
            walletAddress,
            params.depositId,
            callback
          );
        } else if (params.isVip) {
          await lgoWeb3.vipStakingWithdraw(
            walletAddress,
            params.depositId,
            callback
          );
        } else {
          await lgoWeb3.periodicalStakingWithdraw(
            walletAddress,
            params.depositId,
            callback
          );
        }
        return true;
      } catch (error) {
        console.error('Withdraw failed:', error);
        handleTransactionError(error);
        return false;
      }
    },
    [
      walletAddress,
      lgoWeb3,
      setTransactionPending,
      handleTransactionHash,
      handleTransactionSuccess,
      handleTransactionError,
      dispatch,
      t,
    ]
  );

  return {
    transaction,
    stake,
    stakeVip,
    claim,
    claimAll,
    withdraw,
    clearTransaction,
    setTransactionPending,
  };
};
